import { useState, useEffect, useRef, useId } from 'react';
import { X, AlertCircle, RotateCcw } from 'lucide-react';
import {
  BOOKING_DEFAULT_CONFIG,
  BOOKING_DEFAULT_DURATION_MIN,
  calcCourtBookingPrice,
  courtPricesSet,
  courtDisplayName,
  courtOutOfServiceState,
  validateBooking,
  SERIE_DEUCE,
  SERIE_DEUCE_ORDER,
  resolveEffectiveCourts,
  localDateOf,
  availableAssetsFor,
  calcAssetBookingPrice,
} from '../../engine';
import { toMinHHMM, fromMinHHMM } from '../../lib/date';
import { Storage } from '../../lib/storage';
import { useLocale } from '../../i18n';
import { PaymentDisputeNotice, paymentDisputesOf } from './PaymentDisputeNotice';
import { SendPaymentLink } from './SendPaymentLink';
import { CourtFacilityIcons } from './CourtFacilityIcons';
import { Dialog, EloWeightSlider } from '../ui';
import { StaffRefundDialog } from './StaffRefundDialog';
import { paidAmountOf, type StaffRefundResult } from '../../lib/api/staffRefund';
import { safeId } from '../ui/labelledControl';
import { PlayerPicker } from '../singlematches';

/** The footer's message for each reason the modal cannot be saved. */
const INVALID_MESSAGE = {
  duration: 'booking.edit.invalid.duration',
  identity: 'booking.edit.invalid.identity',
  courtTaken: 'booking.edit.invalid.courtTaken',
  signupPassed: 'booking.edit.invalid.signupPassed',
  missing: 'booking.edit.invalid.missing',
} as const;

export interface BookingEditModalProps {
  /** 'create' (new booking) or 'edit' (existing booking). */
  mode?: 'create' | 'edit';
  /** The booking being edited (edit mode), or seed values for a click-to-create. */
  initial?: any;
  /** Physical court count the center has registered. */
  totalCourts?: number;
  /** Default date (YYYY-MM-DD) for a new booking. */
  defaultDate?: string;
  /** Price zones used to compute the live price + breakdown. */
  zones?: any[];
  /** Admin mode unlocks multi-court, custom title/duration, recurrence and status. */
  adminMode?: boolean;
  /** All existing bookings — passed to the validation engine for availability. */
  bookings?: any[];
  /** Booking config (gap rule, durations, opening hours, court meta, …). */
  config?: any;
  /** When editing a series booking: 'series' (whole series) or 'occurrence'. */
  editScope?: 'series' | 'occurrence' | null;
  /** Number of bookings in the series (shown in the series-edit banner / delete copy). */
  seriesCount?: number;
  /** Closes the modal without saving. */
  onClose: () => void;
  /** Persists the booking (parent expands recurrence and opens payment). */
  onSave: (booking: any) => void;
  /** Deletes the booking / series (edit mode only); null hides the delete control. */
  onDelete?: (() => void) | null;
  /**
   * «Avbestill og refunder» (#284): cancels this PAID booking and refunds it in
   * full, server-side. The caller passes it only for a paid single booking and a
   * session holding `payment.refund`; when present it TAKES THE PLACE of the
   * delete control, which the server refuses for a paid booking anyway
   * (`booking_has_payment`). Null keeps the delete control.
   */
  onRefund?: (() => Promise<StaffRefundResult>) | null;
  /** The booking was refunded (or already cancelled) and is gone: close the modal. */
  onRefundDone?: (() => void) | null;
  /**
   * Live player DB — enables the optional "register a match on this booking"
   * step (customer create flow only). When omitted, the step is hidden.
   */
  players?: any[];
  /** Optional: persist a brand-new player created inline from the match picker. */
  onPersistNewPlayer?: ((player: any) => Promise<void> | void) | null;
  // ── Equipment beside the courts (docs/domains/ASSET_BOOKING.md) ──
  /** The bookable assets kept at this venue. Absent or empty hides the section. */
  assets?: any[];
  /** Every asset booking, for availability at the chosen time. */
  assetBookings?: any[];
  /** Edit mode: the equipment already riding with this booking. */
  attachedAssetBookings?: any[];
  /** Edit mode: removes one attached asset booking. */
  onRemoveAssetBooking?: ((id: string) => Promise<any>) | null;
}

/** New/edit booking modal (court picker, smart-booking warnings, live price, recurrence, admin status); extracted verbatim from the original artifact (legacy/App.jsx). */
export function BookingEditModal({
  mode = 'create',
  initial = null,
  totalCourts = 4,
  defaultDate,
  zones = [],
  adminMode = false,
  bookings = [],
  config = null,
  editScope = null,
  seriesCount = 0,
  onClose,
  onSave,
  onDelete = null,
  onRefund = null,
  onRefundDone = null,
  players = [],
  onPersistNewPlayer = null,
  assets = [],
  assetBookings = [],
  attachedAssetBookings = [],
  onRemoveAssetBooking = null,
}: BookingEditModalProps) {
  const { t, formatNumber } = useLocale();
  // ONE id root for every label, name and error this modal wires (#900). The
  // modal can be open twice — the edit modal over a series choice is not, but
  // nothing stops a caller — so nothing here is a literal id.
  const uid = safeId('booking-edit', useId());
  const ids = {
    title: `${uid}-title`,
    confirmTitle: `${uid}-confirm-title`,
    confirmBody: `${uid}-confirm-body`,
    date: `${uid}-date`,
    start: `${uid}-start`,
    duration: `${uid}-duration`,
    court: `${uid}-court`,
    bookingTitle: `${uid}-booking-title`,
    bookingTitleHelp: `${uid}-booking-title-help`,
    bookedBy: `${uid}-booked-by`,
    phone: `${uid}-phone`,
    email: `${uid}-email`,
    notes: `${uid}-notes`,
    notesHelp: `${uid}-notes-help`,
    deregister: `${uid}-deregister`,
    signupClose: `${uid}-signup-close`,
    tooLate: `${uid}-too-late`,
    levelReq: `${uid}-level-req`,
    levelMin: `${uid}-level-min`,
    levelMax: `${uid}-level-max`,
    recurInterval: `${uid}-recur-interval`,
    recurIntervalUnit: `${uid}-recur-interval-unit`,
    recurCount: `${uid}-recur-count`,
    recurCountUnit: `${uid}-recur-count-unit`,
    recurEnds: `${uid}-recur-ends`,
    matchFormat: `${uid}-match-format`,
    deuce: `${uid}-deuce`,
    matchPayment: `${uid}-match-payment`,
    paymentStatus: `${uid}-payment-status`,
    recurFreq: `${uid}-recur-freq`,
    recurDays: `${uid}-recur-days`,
    recurHoliday: `${uid}-recur-holiday`,
    invalid: `${uid}-invalid`,
  };
  const cfg = { ...BOOKING_DEFAULT_CONFIG, ...(config || {}) };
  const allowedDurations =
    cfg.allowedDurations && cfg.allowedDurations.length > 0 ? cfg.allowedDurations : [60, 90, 120];
  // Shortest allowed duration — drives the dead-gap threshold in the gap rule.
  // Shortest allowed duration — ensures the gap rule never allows a hole
  // too short for even the shortest booking. The gap rule (bufferMin)
  // is the admin control; minBookingMin is a technical lower bound.
  const minBookingMin = Math.min(...allowedDurations);
  // THE SHARED ANSWER, not a local one. This fell back to `totalCourts` — the
  // CENTRE's physical count — and ignored the club's own `courtCount`, so a
  // four-court club at a five-court centre was offered «Bane 5» and told it was
  // free. Worse, smart booking scored that empty phantom as the tightest
  // placement and steered every real court towards it.
  const effectiveCourts = resolveEffectiveCourts(cfg, totalCourts);
  const [court, setCourt] = useState(initial?.court || 1);
  // Admin can select multiple courts at once (book them all in one go).
  const [multiCourts, setMultiCourts] = useState(initial?.court ? [initial.court] : [1]);
  // Equipment to book WITH this court booking — a side channel the parent acts
  // on after the court is saved, never a field of the booking record.
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [date, setDate] = useState(
    // LOCAL today: toISOString() gave YESTERDAY in the first hours after
    // midnight in Norway (DOMAIN_RULES §4, #939).
    initial?.date || defaultDate || localDateOf(Date.now()),
  );
  const [startTime, setStartTime] = useState(initial?.startTime || '12:00');
  const [durationMin, setDurationMin] = useState(
    initial?.durationMin || BOOKING_DEFAULT_DURATION_MIN,
  );
  // Custom duration (admin): choose the end time freely instead of a fixed duration.
  const [customDuration, setCustomDuration] = useState(false);
  const [endTimeManual, setEndTimeManual] = useState(initial?.endTime || '13:30');
  const [bookerName, setBookerName] = useState(initial?.bookedBy?.name || '');
  const [bookerPhone, setBookerPhone] = useState(initial?.bookedBy?.phone || '');
  const [bookerEmail, setBookerEmail] = useState(initial?.bookedBy?.email || '');
  // Custom booking title (admin): e.g. "Vedlikehold", "Trening A-lag".
  const [bookingTitle, setBookingTitle] = useState(initial?.title || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  // Optional "register a match on this booking" step (customer create flow only).
  // The booker picks 4 players + an ELO weight; the result is entered later.
  const [registerMatch, setRegisterMatch] = useState(false);
  const [matchTeam1, setMatchTeam1] = useState<{ p1Id: string | null; p2Id: string | null }>({
    p1Id: null,
    p2Id: null,
  });
  const [matchTeam2, setMatchTeam2] = useState<{ p1Id: string | null; p2Id: string | null }>({
    p1Id: null,
    p2Id: null,
  });
  const [matchEloWeightPct, setMatchEloWeightPct] = useState(200);
  // Match rules — the DEFAULT for live scoring on Banepad (changeable there
  // mid-match). matchFormat: best-of-3 with a super-tiebreak (bo3_super) or a
  // full deciding set (bo3_full). deuceMode at 40-40: advantage / golden / starpoint.
  const [matchFormat, setMatchFormat] = useState<'bo3_super' | 'bo3_full'>('bo3_full');
  const [deuceMode, setDeuceMode] = useState<'advantage' | 'golden' | 'starpoint'>('starpoint');
  // How the match is run: 'local' = booker picks players now, result later;
  // 'activity' = an open enkeltmatch-activity (signup/payment/deadline).
  const [matchMode, setMatchMode] = useState<'local' | 'activity'>('local');
  // Via-aktiviteter sub-form (single_match activity). Defaults are short because
  // these matches are usually booked a few hours ahead — a 24h signup deadline
  // on a same-day court would auto-cancel the activity immediately.
  const [matchDeregisterHours, setMatchDeregisterHours] = useState('2'); // withdrawal deadline
  const [matchSignupCloseHours, setMatchSignupCloseHours] = useState('1'); // signup deadline
  const [matchPaymentMode, setMatchPaymentMode] = useState<'split' | 'booker_all'>('split');
  // Level requirement (nivå 1–9) for who may sign up. Empty = all levels.
  const [matchLevelMin, setMatchLevelMin] = useState('');
  const [matchLevelMax, setMatchLevelMax] = useState('');
  // Status: 'free' | 'pending' | 'paid'. Only relevant in admin mode or when
  // the user is editing an existing booking.
  //
  // A club with no saved price zone has no court price (#1312, owner decision
  // 2026-11-03), so a new booking there starts as `free` — nothing is owed, and
  // `pending` with no price is a claim the server refuses.
  const [paymentStatus, setPaymentStatus] = useState(
    initial?.paymentStatus ||
      (mode === 'edit' ? 'free' : courtPricesSet(zones) ? 'pending' : 'free'),
  );
  // Inline delete confirmation (window.confirm does not work in an iframe).
  const [confirmDelete, setConfirmDelete] = useState(false);
  // «Avbestill og refunder» confirmation (#284) — its own dialog, see StaffRefundDialog.
  const [confirmRefund, setConfirmRefund] = useState(false);
  const paidAmount = paidAmountOf(initial);
  // Recurring — Outlook-style recurrence. When editing a whole series
  // (editScope==='series') this is prefilled from the booking's stored recur,
  // so the admin can change the pattern itself.
  const seriesRecur = editScope === 'series' && initial?.recur ? initial.recur : null;
  const [recurring, setRecurring] = useState(!!seriesRecur);
  const [recurFreq, setRecurFreq] = useState(seriesRecur?.freq || 'weekly');
  const [recurInterval, setRecurInterval] = useState(seriesRecur?.interval || 1);
  const [recurWeekdays, setRecurWeekdays] = useState(seriesRecur?.weekdays || []);
  const [recurEndType, setRecurEndType] = useState(seriesRecur?.endType || 'count');
  const [recurCount, setRecurCount] = useState(seriesRecur?.count || 8);
  const [recurUntil, setRecurUntil] = useState(seriesRecur?.until || '');
  const [recurAvoidHolidays, setRecurAvoidHolidays] = useState(seriesRecur?.avoidHolidays || false);

  // Compute endTime live. With custom duration (admin) the manual end time
  // is used; otherwise start + selected duration.
  const useCustom = adminMode && customDuration;
  const endTime = useCustom
    ? endTimeManual
    : fromMinHHMM(toMinHHMM(startTime) + Number(durationMin));
  // Effective duration in minutes (for storage/display).
  const effDuration = useCustom
    ? Math.max(0, toMinHHMM(endTimeManual) - toMinHHMM(startTime))
    : Number(durationMin);

  // Optional "register a match" step — customer create flow only (never admin,
  // edit, or recurring/series bookings). Needs a player DB to pick from.
  const showMatchStep = mode === 'create' && !adminMode && players.length > 0;
  const matchSelectedIds = [
    matchTeam1.p1Id,
    matchTeam1.p2Id,
    matchTeam2.p1Id,
    matchTeam2.p2Id,
  ].filter(Boolean);
  // All four slots filled with DISTINCT players.
  const matchAllSelected = matchSelectedIds.length === 4 && new Set(matchSelectedIds).size === 4;
  // Per-player price for the via-aktiviteter variant = booking price / 4 (fixed).
  // No court price (#1312) is no share either: nothing is charged.
  const matchPerPlayerPrice = Math.round(
    (calcCourtBookingPrice(zones, date, startTime, endTime, cfg)?.total ?? 0) / 4,
  );
  // Match-format options (ids match the engine's KM_MATCH_FORMATS so Banepad +
  // result entry interpret them consistently). Short labels for the single match.
  const MATCH_FORMAT_OPTIONS = [
    {
      id: 'bo3_super',
      label: t('booking.edit.match.format.super'),
      sub: t('booking.edit.match.format.superSub'),
    },
    {
      id: 'bo3_full',
      label: t('booking.edit.match.format.full'),
      sub: t('booking.edit.match.format.fullSub'),
    },
  ];

  const handleSave = () => {
    // Compute the price if a zone is defined — admin needs this information,
    // and edit mode will update the price if time/duration has changed. With no
    // saved zone there is no price (#1312), and the booking carries none: 0,
    // never the default rate or an invented zone's.
    const price = calcCourtBookingPrice(zones, date, startTime, endTime, cfg);
    onSave({
      court: Number(court),
      // Equipment chosen for the booking (docs/domains/ASSET_BOOKING.md). The
      // parent strips it and books each asset attached to the saved court.
      ...(assetIds.length > 0 ? { assetIds } : {}),
      // Admin can book several courts at once. The parent loops over these.
      ...(adminMode && multiCourts.length > 1 ? { courts: multiCourts } : {}),
      date,
      startTime,
      endTime, // IMPORTANT: the parent (BookingView) uses this to compute the price and open the payment modal
      durationMin: effDuration,
      bookedBy: { name: bookerName.trim(), phone: bookerPhone.trim(), email: bookerEmail.trim() },
      // Custom title (admin) — shown on the booking so it's clear it's an admin booking.
      ...(adminMode && bookingTitle.trim() ? { title: bookingTitle.trim() } : {}),
      notes: notes.trim(),
      // Origin: admin mode marks the booking as center/admin, otherwise user.
      // (The tournament integration will set source:'tournament' when created.)
      ...(mode === 'create' ? { source: adminMode ? 'admin' : 'user' } : {}),
      // In admin mode or edit mode the status is controlled manually.
      ...(adminMode || mode === 'edit' ? { paymentStatus, priceNok: price?.total ?? 0 } : {}),
      // Recurring (Outlook-style): admin create mode, OR editing a whole
      // series (editScope==='series') where the pattern can be changed. The parent
      // expands the occurrences from this rule set.
      ...(adminMode && (mode === 'create' || editScope === 'series') && recurring
        ? {
            recur: {
              enabled: true,
              freq: recurFreq,
              interval: Math.max(1, Number(recurInterval) || 1),
              weekdays:
                recurFreq === 'weekly'
                  ? recurWeekdays.length > 0
                    ? recurWeekdays
                    : [(new Date(date + 'T00:00:00').getDay() + 6) % 7]
                  : [],
              endType: recurEndType,
              count: Number(recurCount) || 1,
              until: recurUntil || null,
              avoidHolidays: recurAvoidHolidays,
            },
          }
        : {}),
      // Optional LOCAL planned single match (booker picks players now, result
      // later). Side-channel on the booking payload; BookingView strips it and
      // registers it against the created booking id. Only when four distinct
      // players are chosen (otherwise silently skipped — never blocks the booking).
      ...(showMatchStep && registerMatch && matchMode === 'local' && matchAllSelected
        ? {
            plannedMatch: {
              team1: { p1Id: matchTeam1.p1Id, p2Id: matchTeam1.p2Id },
              team2: { p1Id: matchTeam2.p1Id, p2Id: matchTeam2.p2Id },
              eloWeightPct: matchEloWeightPct,
              matchFormat,
              deuceMode,
            },
          }
        : {}),
      // Optional VIA-AKTIVITETER single-match activity (open signup/payment).
      // Side-channel; BookingView creates the activity + links this booking and
      // (for split payment) skips the normal full upfront charge.
      ...(showMatchStep && registerMatch && matchMode === 'activity'
        ? {
            activityMatch: {
              enabled: true,
              eloWeightPct: matchEloWeightPct,
              matchFormat,
              deuceMode,
              paymentMode: matchPaymentMode,
              deregisterCutoffHours: Math.max(0, Number(matchDeregisterHours) || 0),
              signupCloseHours: Math.max(0, Number(matchSignupCloseHours) || 0),
              pricePerPlayer: Math.round(price.total / 4),
              levelMin: matchLevelMin ? Number(matchLevelMin) : null,
              levelMax: matchLevelMax ? Number(matchLevelMax) : null,
            },
          }
        : {}),
    });
  };

  // Check whether the selected court is actually bookable at the chosen time (full
  // validation incl. smart booking). Used to show a clear message when
  // the user has clicked a court that isn't possible, and to block saving.
  const selectedCourtCheck = validateBooking(
    { id: initial?.id, court: Number(court), date, startTime, endTime },
    bookings,
    {
      bufferMin: cfg.bufferMin,
      minBookingMin,
      smartAllocation: cfg.smartAllocation !== false,
      totalCourts: effectiveCourts.length,
      enabledCourts: effectiveCourts,
      allowedDurations,
    },
  );
  // Pure overlap check — the only hard limit in admin mode.
  const selectedOverlapCheck = validateBooking(
    { id: initial?.id, court: Number(court), date, startTime, endTime },
    bookings,
    { bufferMin: 0, smartAllocation: false },
  );
  // In admin mode the choice is valid as long as there's no direct overlap.
  // Gap/smart violations only produce a warning (see banner below), not a block.
  const selectedCourtOk = adminMode ? selectedOverlapCheck.ok : selectedCourtCheck.ok;
  // Admin warning: the choice is allowed, but breaks a rule (gap or smart).
  const adminOverrideWarning = adminMode && selectedOverlapCheck.ok && !selectedCourtCheck.ok;

  // Check whether a given duration has at least one bookable court at the chosen start/date.
  // Used to disable impossible durations in the dropdown. In admin mode
  // a court counts as "possible" as long as there's no direct overlap.
  const durationHasOption = (durMin) => {
    const candEnd = fromMinHHMM(toMinHHMM(startTime) + Number(durMin));
    return effectiveCourts.some((c) => {
      const cand = { id: initial?.id, court: c, date, startTime, endTime: candEnd };
      if (adminMode) {
        return validateBooking(cand, bookings, { bufferMin: 0, smartAllocation: false }).ok;
      }
      return validateBooking(cand, bookings, {
        bufferMin: cfg.bufferMin,
        minBookingMin,
        smartAllocation: cfg.smartAllocation !== false,
        totalCourts: effectiveCourts.length,
        enabledCourts: effectiveCourts,
        allowedDurations,
      }).ok;
    });
  };

  // On open (create mode): if the preselected duration isn't possible at the
  // chosen start (e.g. from a click in the calendar), switch to the first
  // allowed duration that actually has a free court. Runs only once.
  const didInitDuration = useRef(false);
  useEffect(() => {
    if (mode !== 'create' || didInitDuration.current) return;
    didInitDuration.current = true;
    if (!durationHasOption(durationMin)) {
      // Pick the allowed duration with a free court that's CLOSEST to the desired
      // duration, preferring the longer one on a tie (matches the hover box
      // that shows the longest possible). Avoids dropping to 60 when 120 fits.
      const cur = Number(durationMin);
      const okDurs = allowedDurations.filter((d) => durationHasOption(d));
      if (okDurs.length) {
        okDurs.sort((a, b) => Math.abs(a - cur) - Math.abs(b - cur) || b - a);
        setDurationMin(okDurs[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When recurring + weekly: preselect the weekday matching the start date
  // if no days are selected yet. (Mon=0 .. Sun=6)
  useEffect(() => {
    if (recurring && recurFreq === 'weekly' && recurWeekdays.length === 0) {
      const wd = (new Date(date + 'T00:00:00').getDay() + 6) % 7;
      setRecurWeekdays([wd]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recurring, recurFreq, date]);

  // When the booker enables "register a match", suggest the booker as Hjemmelag
  // → Spiller A. The booker = the "logged-in-as" profile shared with Aktiviteter
  // (persisted under demo_user_v1); falls back to the favourite/first player. It
  // is only a suggestion — the booker can change it. Runs once per enable so it
  // never fights a manual pick.
  const didPrefillBooker = useRef(false);
  useEffect(() => {
    if (!registerMatch) {
      didPrefillBooker.current = false;
      return undefined;
    }
    if (didPrefillBooker.current || matchTeam1.p1Id) return undefined;
    let cancelled = false;
    (async () => {
      let id: string | null = null;
      try {
        const r = await Storage.get('demo_user_v1');
        if (r?.value) id = JSON.parse(r.value);
      } catch {
        /* ignore: best-effort */
      }
      const exists = (pid: string | null) => !!pid && (players || []).some((p) => p.id === pid);
      if (!exists(id)) {
        const fav = (players || []).find((p) => p.isFavorite);
        id = fav?.id || players?.[0]?.id || null;
      }
      if (cancelled || !id) return;
      const taken = [matchTeam1.p2Id, matchTeam2.p1Id, matchTeam2.p2Id];
      if (taken.includes(id)) return;
      didPrefillBooker.current = true;
      setMatchTeam1((t) => ({ ...t, p1Id: id }));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerMatch, players]);

  // In admin and edit mode we require the admin to fill in a name (they book on
  // behalf of others). In the customer flow the name comes from the logged-in user, so
  // Valid if duration > 0 and (for non-recurring) the selected court is ok.
  // For recurring, each occurrence is evaluated separately at save time, so we
  // don't require the start date to be free here. Admin must have either a name OR a title.
  const hasIdentity =
    adminMode || mode === 'edit'
      ? bookerName.trim().length > 0 || (adminMode && bookingTitle.trim().length > 0)
      : true;
  // Via-aktiviteter needs time for players to sign up: the signup deadline
  // (start − påmeldingsfrist) must be in the FUTURE. If the booking is so soon
  // that the deadline has already passed, the activity would auto-cancel at once
  // — so block registration and tell the booker to pick a later slot, shorten
  // the deadline, or enter the players themselves.
  const matchActivityTooLate = (() => {
    if (!(showMatchStep && registerMatch && matchMode === 'activity')) return false;
    const startMs = new Date(`${date}T${startTime}:00`).getTime();
    if (isNaN(startMs)) return false;
    const deadlineMs = startMs - Math.max(0, Number(matchSignupCloseHours) || 0) * 3600000;
    return deadlineMs <= Date.now();
  })();
  const isValid =
    effDuration > 0 && (recurring ? true : selectedCourtOk) && hasIdentity && !matchActivityTooLate;
  // WHICH RULE the footer message is about — the same cascade it rendered
  // inline, kept in one place so the field marked invalid is the field the
  // message names (#900).
  const invalidReason: 'duration' | 'identity' | 'courtTaken' | 'signupPassed' | 'missing' | null =
    isValid
      ? null
      : effDuration <= 0
        ? 'duration'
        : !hasIdentity
          ? 'identity'
          : !recurring && !selectedCourtOk
            ? 'courtTaken'
            : matchActivityTooLate
              ? 'signupPassed'
              : 'missing';
  // The admin-only extra durations, and whether ANY duration is bookable at
  // this start. Lifted out of the duration field so its label knows whether a
  // control renders at all.
  const durationExtras = adminMode
    ? [30, 45, 150, 180, 240].filter((d) => !allowedDurations.includes(d))
    : [];
  const durationAnyOk =
    allowedDurations.some(durationHasOption) || durationExtras.some(durationHasOption);

  // BOTH OVERLAYS ARE `Dialog`s (#900): named by their own visible titles,
  // focus moved in and kept there, Escape and the backdrop dismiss as the
  // backdrop always did, and focus goes back to the control that opened them.
  // The inline styles are the ones the hand-built overlays had, moved verbatim.
  //
  // THE CANCELLATION CONFIRM IS A SIBLING, not a child. It used to sit inside
  // this modal's backdrop and stop its own clicks from reaching it; as a
  // sibling it is simply the innermost open dialog, which is the shape
  // `Dialog`'s keyboard ownership is built for. Escape there means «Avbryt» —
  // the same as its backdrop — and focus starts on «Avbryt», the first control,
  // so the destructive answer is never the one a stray Enter gives.
  return (
    <>
      {confirmDelete && (
        <Dialog
          labelledBy={ids.confirmTitle}
          describedBy={ids.confirmBody}
          onClose={() => setConfirmDelete(false)}
          showClose={false}
          backdropClassName=""
          backdropStyle={{
            position: 'fixed',
            inset: 0,
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(15,22,17,0.6)',
          }}
          className="overflow-x-hidden overflow-y-auto max-h-[92vh]"
          style={{
            maxHeight: 'calc(100dvh - 2rem)',
            background: '#fff',
            maxWidth: 360,
            width: '100%',
            borderRadius: 16,
            boxShadow: '0 25px 60px -10px rgba(0,0,0,0.45)',
          }}
        >
          <div style={{ padding: '18px 20px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#fee2e2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AlertCircle size={20} style={{ color: '#dc2626' }} />
              </span>
              <h3
                id={ids.confirmTitle}
                className="font-display text-lg font-semibold"
                style={{ color: '#1a1f1c' }}
              >
                {t(
                  editScope === 'series'
                    ? 'booking.edit.confirm.seriesTitle'
                    : 'booking.edit.confirm.title',
                )}
              </h3>
            </div>
            <p id={ids.confirmBody} className="text-sm text-ink-2" style={{ lineHeight: 1.45 }}>
              {editScope === 'series'
                ? t('booking.edit.confirm.seriesBody', { count: String(seriesCount) })
                : t('booking.edit.confirm.body')}
              {/* Equipment riding with this booking goes with it (ON DELETE CASCADE,
                  docs/domains/ASSET_BOOKING.md) — said here, before the delete. */}
              {editScope !== 'series' && attachedAssetBookings.length > 0 && (
                <>
                  {' '}
                  {t('booking.edit.confirm.withAssets', {
                    n: String(attachedAssetBookings.length),
                  })}
                </>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, padding: '0 20px 18px' }}>
            <button
              onClick={() => setConfirmDelete(false)}
              className="flex-1 text-sm font-semibold py-2.5 rounded-lg"
              style={{ border: '1px solid #d7dad3', color: '#4a544c', background: '#fff' }}
            >
              {t('booking.cancel')}
            </button>
            <button
              onClick={onDelete}
              className="flex-1 text-sm font-semibold py-2.5 rounded-lg"
              style={{ background: '#dc2626', color: '#fff', border: 'none' }}
            >
              {t('booking.edit.confirm.confirm')}
            </button>
          </div>
        </Dialog>
      )}
      {confirmRefund && onRefund && (
        <StaffRefundDialog
          amount={paidAmount}
          attachedAssets={attachedAssetBookings.length}
          onRefund={onRefund}
          onClose={() => setConfirmRefund(false)}
          onDone={() => {
            setConfirmRefund(false);
            (onRefundDone ?? onClose)();
          }}
        />
      )}
      <Dialog
        labelledBy={ids.title}
        onClose={onClose}
        showClose={false}
        backdropClassName=""
        backdropStyle={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          background: 'rgba(15, 22, 17, 0.55)',
        }}
        className=""
        style={{
          background: '#ffffff',
          maxWidth: 480,
          width: '100%',
          maxHeight: 'calc(100dvh - 100px)',
          marginTop: 80,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
          border: '1px solid #e7eae3',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e7eae3',
            background: '#fafbf7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h3 id={ids.title} className="font-display text-lg font-semibold leading-tight">
            {t(mode === 'create' ? 'booking.edit.titleCreate' : 'booking.edit.titleEdit')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('booking.close')}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#4a544c',
              padding: 4,
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: '1 1 auto' }} className="space-y-3">
          {editScope && (
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${editScope === 'series' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-bg-soft/40 border border-line text-ink-2'}`}
            >
              <RotateCcw size={14} className="flex-shrink-0" />
              {editScope === 'series' ? (
                <span>
                  {t('booking.edit.scope.seriesBefore')}{' '}
                  <strong>{t('booking.edit.scope.seriesStrong')}</strong>{' '}
                  {t('booking.edit.scope.seriesAfter', { count: String(seriesCount) })}
                </span>
              ) : (
                <span>
                  {t('booking.edit.scope.occBefore')}{' '}
                  <strong>{t('booking.edit.scope.occStrong')}</strong>{' '}
                  {t('booking.edit.scope.occAfter')}
                </span>
              )}
            </div>
          )}
          {/* Date and time first — the court is chosen based on these */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label
                htmlFor={ids.date}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.edit.date')}
              </label>
              <input
                id={ids.date}
                type="date"
                className="input w-full"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div>
              {/* Two selects, so the visible «Start» names the GROUP and each select
                  says which half it is (#900). A <div>, not a <label>: a label
                  can only name one control. */}
              <div
                id={ids.start}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.edit.start')}
              </div>
              {(() => {
                // Two dropdowns instead of a native time input: hours (within
                // opening hours) + minutes (only 00/30, since bookings must start
                // on the hour/half hour). Guarantees valid values.
                const [sh, sm] = startTime.split(':').map(Number);
                const openMin = toMinHHMM(cfg.openTime);
                let closeMin = toMinHHMM(cfg.closeTime);
                // Past midnight / 24-hour: closing at/before opening = next day.
                if (closeMin <= openMin) closeMin += 1440;
                const openH = Math.floor(openMin / 60);
                const closeH = Math.ceil(closeMin / 60);
                const hourOpts = [];
                for (let h = openH; h <= closeH; h++) hourOpts.push(h % 24);
                // Ensure the current hour always exists as an option, even
                // if the opening hours changed after startTime was set.
                if (!hourOpts.includes(sh)) hourOpts.unshift(sh);
                // Deduplicate (past-midnight can produce repeats) and preserve order.
                const uniqHours = [...new Set(hourOpts)];
                const setPart = (h, m) =>
                  setStartTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
                // Two-digit selects in a narrow column: the native arrow reserves
                // ~20px on top of the padding and clipped the digits («1:» / «0(» on
                // an 848px-wide Edge window). Draw our own slim chevron instead.
                const timeSelectStyle = {
                  minWidth: '3.25rem',
                  paddingLeft: '0.45rem',
                  paddingRight: '1.1rem',
                  appearance: 'none' as const,
                  WebkitAppearance: 'none' as const,
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'><path d='M2 3.5l3 3 3-3' fill='none' stroke='%236b746d' stroke-width='1.6' stroke-linecap='round'/></svg>\")",
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.3rem center',
                  backgroundSize: '10px 10px',
                };
                return (
                  <div className="flex items-center gap-1" role="group" aria-labelledby={ids.start}>
                    <select
                      aria-label={t('booking.edit.startHour')}
                      className="input flex-1 tabular-nums"
                      style={timeSelectStyle}
                      value={sh}
                      onChange={(e) => setPart(Number(e.target.value), sm)}
                    >
                      {uniqHours.map((h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <span className="text-ink-3 font-semibold">:</span>
                    <select
                      aria-label={t('booking.edit.startMinute')}
                      className="input flex-1 tabular-nums"
                      style={timeSelectStyle}
                      value={sm}
                      onChange={(e) => setPart(sh, Number(e.target.value))}
                    >
                      <option value={0}>00</option>
                      <option value={30}>30</option>
                    </select>
                  </div>
                );
              })()}
            </div>
            <div>
              <label
                // Only when a control renders: with nothing bookable the field
                // is a grey notice, and a `htmlFor` at nothing is not a label.
                htmlFor={useCustom || durationAnyOk ? ids.duration : undefined}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
              >
                {t('booking.edit.duration')}
              </label>
              {useCustom ? (
                <div className="flex items-center gap-1">
                  <span className="text-[11px] text-ink-3">{t('booking.edit.until')}</span>
                  <input
                    id={ids.duration}
                    type="time"
                    step="1800"
                    className="input flex-1"
                    aria-invalid={invalidReason === 'duration' || undefined}
                    aria-describedby={invalidReason === 'duration' ? ids.invalid : undefined}
                    value={endTimeManual}
                    onChange={(e) => setEndTimeManual(e.target.value)}
                  />
                </div>
              ) : (
                (() => {
                  const extras = durationExtras;
                  if (!durationAnyOk) {
                    // No duration is bookable at the chosen start/date → gray out the field.
                    return (
                      <div
                        className="input w-full flex items-center text-sm text-ink-3 cursor-not-allowed select-none"
                        style={{ background: 'var(--bg-soft, #f1f1f1)', opacity: 0.7 }}
                        aria-disabled="true"
                      >
                        {t('booking.edit.noneFree')}
                      </div>
                    );
                  }
                  return (
                    <select
                      id={ids.duration}
                      className="input w-full"
                      value={durationMin}
                      onChange={(e) => setDurationMin(Number(e.target.value))}
                    >
                      {allowedDurations.map((d) => {
                        const ok = durationHasOption(d);
                        return (
                          <option key={d} value={d} disabled={!ok}>
                            {t(
                              ok
                                ? 'booking.edit.durationOption'
                                : 'booking.edit.durationOptionBusy',
                              { n: String(d) },
                            )}
                          </option>
                        );
                      })}
                      {extras.map((d) => {
                        const ok = durationHasOption(d);
                        return (
                          <option key={d} value={d} disabled={!ok}>
                            {t(
                              ok
                                ? 'booking.edit.durationOptionAdmin'
                                : 'booking.edit.durationOptionAdminBusy',
                              { n: String(d) },
                            )}
                          </option>
                        );
                      })}
                    </select>
                  );
                })()
              )}
              {adminMode && (
                <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customDuration}
                    onChange={(e) => setCustomDuration(e.target.checked)}
                    className="w-3.5 h-3.5 accent-emerald-600"
                  />
                  <span className="text-[10px] text-ink-3">{t('booking.edit.customEnd')}</span>
                </label>
              )}
            </div>
          </div>

          {/* When the selected duration has NO free court, but another
              allowed duration at the same start works: suggest it with an
              adjust button. E.g. on a 2-hour opening where the user picked 90
              min (dead gap) — we suggest 120 min, which fits. */}
          {!durationHasOption(durationMin) &&
            (() => {
              // Find durations that actually have a free court at this start.
              const candidates = [
                ...new Set([...allowedDurations, ...(adminMode ? [30, 45, 150, 180, 240] : [])]),
              ]
                .filter((d) => d !== Number(durationMin) && durationHasOption(d))
                .sort((a, b) => {
                  // Sort by proximity to the selected duration, prefer longer on a tie
                  const da = Math.abs(a - Number(durationMin)),
                    db = Math.abs(b - Number(durationMin));
                  return da - db || b - a;
                });
              if (candidates.length === 0) return null;
              const suggest = candidates[0];
              return (
                <div className="px-3 py-2.5 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="mb-1.5">
                      {t('booking.edit.suggest.body', {
                        duration: String(durationMin),
                        start: startTime,
                        suggest: String(suggest),
                      })}
                    </div>
                    <button
                      onClick={() => setDurationMin(suggest)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-sky-600 text-white hover:bg-sky-700 transition-colors"
                    >
                      {t('booking.edit.suggest.adjust', { suggest: String(suggest) })}
                    </button>
                  </div>
                </div>
              );
            })()}

          {/* Message when the selected/clicked court isn't possible — e.g. when
              a busy or smart-locked court was clicked in the calendar. Asks
              the user to pick another court (the green ones below). */}
          {!selectedCourtOk &&
            durationHasOption(durationMin) &&
            (() => {
              // Determine why the selected court fails, to give a precise explanation.
              const gapOnly = validateBooking(
                { id: initial?.id, court: Number(court), date, startTime, endTime },
                bookings,
                { bufferMin: cfg.bufferMin, minBookingMin, smartAllocation: false },
              );
              const deadGap = !gapOnly.ok && gapOnly.reason && /hull/i.test(gapOnly.reason);
              const deadMin = deadGap ? (gapOnly.reason.match(/(\d+)\s*min/) || [])[1] : null;
              return (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    {selectedCourtCheck.betterCourt ? (
                      <>
                        {t('booking.edit.warn.betterBefore', { court: String(court) })}{' '}
                        <strong>
                          {t('booking.edit.warn.betterCourt', {
                            court: String(selectedCourtCheck.betterCourt),
                          })}
                        </strong>{' '}
                        {t('booking.edit.warn.betterAfter')}
                      </>
                    ) : deadGap ? (
                      <>
                        {t('booking.edit.warn.deadGap', {
                          court: String(court),
                          start: startTime,
                          end: endTime,
                          gap: t(
                            deadMin ? 'booking.edit.warn.gapOf' : 'booking.edit.warn.gapSmall',
                            { min: String(deadMin) },
                          ),
                        })}
                      </>
                    ) : (
                      <>
                        {t('booking.edit.warn.busy', {
                          court: String(court),
                          start: startTime,
                          end: endTime,
                        })}
                      </>
                    )}
                  </span>
                </div>
              );
            })()}

          {/* Admin warning: the choice is allowed (admin overrides), but breaks a
              rule. We don't block — just inform that a smarter choice
              exists, so the admin can decide deliberately. */}
          {adminOverrideWarning &&
            (() => {
              const gapOnly = validateBooking(
                { id: initial?.id, court: Number(court), date, startTime, endTime },
                bookings,
                { bufferMin: cfg.bufferMin, minBookingMin, smartAllocation: false },
              );
              const deadGap = !gapOnly.ok && gapOnly.reason && /hull/i.test(gapOnly.reason);
              const deadMin = deadGap ? (gapOnly.reason.match(/(\d+)\s*min/) || [])[1] : null;
              return (
                <div className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-start gap-2">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{t('booking.edit.adminOverride.label')}</strong>{' '}
                    {selectedCourtCheck.betterCourt ? (
                      <>
                        {t('booking.edit.adminOverride.better', {
                          court: String(court),
                          better: String(selectedCourtCheck.betterCourt),
                        })}
                      </>
                    ) : deadGap ? (
                      <>
                        {t('booking.edit.adminOverride.deadGap', {
                          gap: t(
                            deadMin ? 'booking.edit.warn.gapOf' : 'booking.edit.warn.gapSmall',
                            { min: String(deadMin) },
                          ),
                        })}
                      </>
                    ) : (
                      <>{t('booking.edit.adminOverride.smart')}</>
                    )}
                  </span>
                </div>
              );
            })()}

          {/* Pick court: all courts as clickable cards. Free ones are clickable/colored,
              busy ones are disabled and show why + the next free time. */}
          <div>
            <div
              id={ids.court}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
            >
              {adminMode && multiCourts.length > 1
                ? t('booking.edit.chooseCourtCount', { n: String(multiCourts.length) })
                : t('booking.edit.chooseCourt')}
            </div>
            {adminMode && (
              <p className="text-[11px] text-ink-3 mb-1.5">{t('booking.edit.multiCourtHint')}</p>
            )}
            {/* Explanation of smart booking — shown only when there actually are
                courts that are locked (smart-locked or dead gap), so the user
                understands it's not a bug, but a deliberate allocation. */}
            {cfg.smartAllocation !== false &&
              effectiveCourts.some((c) => {
                const cand = { id: initial?.id, court: c, date, startTime, endTime };
                const full = validateBooking(cand, bookings, {
                  bufferMin: cfg.bufferMin,
                  minBookingMin,
                  smartAllocation: true,
                  totalCourts: effectiveCourts.length,
                  enabledCourts: effectiveCourts,
                });
                const gap = validateBooking(cand, bookings, {
                  bufferMin: cfg.bufferMin,
                  minBookingMin,
                  smartAllocation: false,
                });
                // Locked = smart-locked (gap-legal but a better court exists) OR dead gap
                const smartLocked = !full.ok && gap.ok && full.betterCourt;
                const deadGap = !gap.ok && gap.reason && /hull/i.test(gap.reason);
                return smartLocked || deadGap;
              }) && (
                <details className="mb-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 overflow-hidden group">
                  <summary className="px-3 py-2 text-[11px] font-semibold cursor-pointer flex items-center gap-2 list-none select-none hover:bg-emerald-100/50 transition-colors">
                    <span className="text-sm">💡</span>
                    <span className="flex-1">{t('booking.edit.smartInfo.summary')}</span>
                    <span className="text-emerald-600 text-[10px] group-open:rotate-180 transition-transform">
                      ▼
                    </span>
                  </summary>
                  <div className="px-3 pb-3 pt-0 text-[11px] leading-relaxed text-emerald-800/90 space-y-1.5">
                    <p>
                      <strong>{t('booking.edit.smartInfo.question')}</strong>
                    </p>
                    <p>{t('booking.edit.smartInfo.p1')}</p>
                    <p>
                      {t('booking.edit.smartInfo.p2Before')}{' '}
                      <strong>{t('booking.edit.smartInfo.p2Strong')}</strong>{' '}
                      {t('booking.edit.smartInfo.p2After')}
                    </p>
                  </div>
                </details>
              )}
            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-2"
              role="group"
              aria-labelledby={ids.court}
              aria-describedby={invalidReason === 'courtTaken' ? ids.invalid : undefined}
            >
              {effectiveCourts.map((c) => {
                // Check whether this court is free at the chosen time (full check
                // incl. smart booking).
                const candidate = { id: initial?.id, court: c, date, startTime, endTime };
                const v = validateBooking(candidate, bookings, {
                  bufferMin: cfg.bufferMin,
                  minBookingMin,
                  smartAllocation: cfg.smartAllocation !== false,
                  totalCourts: effectiveCourts.length,
                  enabledCourts: effectiveCourts,
                });
                // Pure overlap check (no gap/smart) — the only hard limit for admin.
                const overlapV = validateBooking(candidate, bookings, {
                  bufferMin: 0,
                  smartAllocation: false,
                });
                const hasOverlap = !overlapV.ok;
                // In admin mode you can pick anything that isn't a direct overlap —
                // gap and smart rules only produce a warning, not a block.
                const isAvailable = adminMode ? !hasOverlap : v.ok;
                const isSelected = adminMode ? multiCourts.includes(c) : c === Number(court);
                // Three reasons a court isn't selectable:
                //  1) isSmartLocked — gap-legal, but another court is tighter
                //  2) isDeadGap     — this booking would create a dead gap
                //                     (too small for anyone else to fit)
                //  3) truly busy    — overlap with an existing booking
                const gapV = validateBooking(candidate, bookings, {
                  bufferMin: cfg.bufferMin,
                  minBookingMin,
                  smartAllocation: false,
                });
                const isSmartLocked = !v.ok && gapV.ok && v.betterCourt;
                // Dead gap: gap validation fails, but NOT due to direct overlap.
                const isDeadGap =
                  !gapV.ok && !hasOverlap && gapV.reason && /hull/i.test(gapV.reason);
                // How big is the dead gap? Extract the number from the message.
                const deadGapMin = isDeadGap ? (gapV.reason.match(/(\d+)\s*min/) || [])[1] : null;
                // In admin mode, smart-locked/dead-gap courts are still selectable,
                // but marked as "warning" (admin can override).
                const isAdminWarn = adminMode && (isSmartLocked || isDeadGap);
                // OUT OF SERVICE (#271) — a fact about the COURT, not about this
                // time. It does not change what is selectable: the court is still
                // offered, and the server decides whether the booking stands.
                const broken = courtOutOfServiceState(c, cfg.courtMeta || {});

                // For truly busy courts: find the next free time going forward with the same duration
                let nextFreeText = null;
                if (!isAvailable && !isSmartLocked && !isDeadGap) {
                  const closeMin = toMinHHMM(cfg.closeTime);
                  const dur = Number(durationMin);
                  const startSearch = toMinHHMM(startTime) + 30;
                  // `tMin`, not `t`: the translator is in scope here, and a
                  // loop counter called `t` would shadow it.
                  for (let tMin = startSearch; tMin + dur <= closeMin; tMin += 30) {
                    const tryStart = fromMinHHMM(tMin);
                    const tryEnd = fromMinHHMM(tMin + dur);
                    const tryV = validateBooking(
                      { id: initial?.id, court: c, date, startTime: tryStart, endTime: tryEnd },
                      bookings,
                      { bufferMin: cfg.bufferMin, minBookingMin },
                    );
                    if (tryV.ok) {
                      nextFreeText = t('booking.edit.nextFree', { time: tryStart });
                      break;
                    }
                  }
                  if (!nextFreeText) nextFreeText = t('booking.edit.noneLeftToday');
                }

                return (
                  <button
                    key={c}
                    onClick={() => {
                      if (!isAvailable) return;
                      if (adminMode) {
                        // Toggle in the multi-selection + keep 'court' synced to the first selected.
                        setMultiCourts((prev) => {
                          const next = prev.includes(c)
                            ? prev.filter((x) => x !== c)
                            : [...prev, c].sort((a, b) => a - b);
                          setCourt(next[0] || c);
                          return next.length > 0 ? next : [c];
                        });
                      } else {
                        setCourt(c);
                      }
                    }}
                    disabled={!isAvailable}
                    aria-pressed={isSelected}
                    className={`relative rounded-lg p-3 text-left border-2 transition-all ${
                      isSelected && isAvailable
                        ? 'border-emerald-500 bg-emerald-50 shadow-md ring-2 ring-emerald-200'
                        : isAdminWarn && isSelected
                          ? 'border-amber-400 bg-amber-50 shadow-md ring-2 ring-amber-200 cursor-pointer'
                          : isAdminWarn
                            ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400 cursor-pointer'
                            : isAvailable
                              ? 'border-line bg-white hover:border-emerald-300 hover:bg-emerald-50/40 cursor-pointer'
                              : isSmartLocked || isDeadGap
                                ? 'border-amber-200 bg-amber-50/40 opacity-80 cursor-not-allowed'
                                : 'border-line-soft bg-bg-soft/30 opacity-70 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`flex items-center gap-1.5 font-display text-base font-semibold ${isAvailable && !isAdminWarn ? 'text-ink-1' : isAdminWarn ? 'text-amber-900' : 'text-ink-3'}`}
                      >
                        {cfg.courtMeta?.[c]?.sponsorLogo && (
                          <img
                            src={cfg.courtMeta[c].sponsorLogo}
                            alt=""
                            className="h-4 w-auto object-contain"
                          />
                        )}
                        {courtDisplayName(c, cfg.courtMeta || {})}
                      </span>
                      {broken ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          {t('booking.edit.outOfService')}
                        </span>
                      ) : isAvailable && !isAdminWarn ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                          {t('booking.edit.courtFree')}
                        </span>
                      ) : isSmartLocked ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          {t(
                            adminMode
                              ? 'booking.edit.betterBadgeAdmin'
                              : 'booking.edit.betterBadge',
                            { court: String(v.betterCourt) },
                          )}
                        </span>
                      ) : isDeadGap ? (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          {t(adminMode ? 'booking.edit.gapBadgeAdmin' : 'booking.edit.gapBadge')}
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-soft text-rose">
                          {t('booking.edit.courtBusy')}
                        </span>
                      )}
                    </div>
                    <CourtFacilityIcons
                      courtNum={c}
                      courtMeta={cfg.courtMeta}
                      courtDefaults={cfg.courtDefaults}
                      size={12}
                      className="mt-1"
                    />
                    {broken && (
                      <div className="text-[10px] text-amber-800 mt-1 leading-tight">
                        {broken.reason
                          ? t('booking.edit.outOfServiceNoteReason', { reason: broken.reason })
                          : t('booking.edit.outOfServiceNote')}
                      </div>
                    )}
                    {cfg.courtMeta?.[c]?.sponsorName && (
                      <div className="text-[10px] text-ink-3 mt-0.5">
                        {t('booking.edit.sponsoredBy', {
                          sponsor: cfg.courtMeta[c].sponsorName,
                        })}
                      </div>
                    )}
                    {isSmartLocked ? (
                      <div className="text-[10px] text-amber-700 mt-1.5 leading-tight">
                        {adminMode ? (
                          <>
                            {t('booking.edit.smartLockedAdmin', {
                              court: String(v.betterCourt),
                            })}
                          </>
                        ) : (
                          <>{t('booking.edit.smartLocked', { court: String(v.betterCourt) })}</>
                        )}
                      </div>
                    ) : isDeadGap ? (
                      <div className="text-[10px] text-amber-700 mt-1.5 leading-tight">
                        {adminMode ? (
                          <>
                            {t('booking.edit.deadGapAdmin', {
                              gap: t(
                                deadGapMin
                                  ? 'booking.edit.gapMinutes'
                                  : 'booking.edit.gapSmallHole',
                                { min: String(deadGapMin) },
                              ),
                            })}
                          </>
                        ) : (
                          <>
                            {t('booking.edit.deadGap', {
                              gap: t(
                                deadGapMin
                                  ? 'booking.edit.gapMinutes'
                                  : 'booking.edit.gapSmallHole',
                                { min: String(deadGapMin) },
                              ),
                            })}
                          </>
                        )}
                      </div>
                    ) : (
                      !isAvailable && (
                        <div className="text-[10px] text-ink-3 mt-1.5 leading-tight">
                          {nextFreeText}
                        </div>
                      )
                    )}
                  </button>
                );
              })}
            </div>
            {/* Check whether anything is free at all */}
            {(() => {
              const anyAvailable = effectiveCourts.some((c) => {
                const v = validateBooking(
                  { id: initial?.id, court: c, date, startTime, endTime },
                  bookings,
                  {
                    bufferMin: cfg.bufferMin,
                    minBookingMin,
                    smartAllocation: cfg.smartAllocation !== false,
                  },
                );
                return v.ok;
              });
              if (!anyAvailable) {
                return (
                  <div className="mt-2 px-3 py-2 rounded-lg bg-rose-soft text-rose text-xs font-semibold flex items-center gap-2">
                    <AlertCircle size={14} className="flex-shrink-0" />
                    {t('booking.edit.noCourtsFree')}
                  </div>
                );
              }
              return null;
            })()}
          </div>

          <div className="text-[11px] text-ink-3 italic">
            {t('booking.edit.summary', {
              start: startTime,
              end: endTime,
              duration: String(durationMin),
            })}
          </div>

          {/* Live price info based on price zones + default price. A club
              with no saved zone has no price at all (#1312): it says so,
              rather than showing the default rate or «free». */}
          {startTime && endTime && !courtPricesSet(zones) && (
            <div className="rounded-lg p-2.5 border bg-bg-soft/40 border-line">
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                  {t('booking.edit.price')}
                </span>
                <span className="font-display text-lg font-bold text-ink-2">
                  {t('booking.edit.priceNotSet')}
                </span>
              </div>
              <div className="mt-1 text-[10px] text-ink-3">{t('booking.edit.priceNotSetHint')}</div>
            </div>
          )}
          {courtPricesSet(zones) &&
            startTime &&
            endTime &&
            (() => {
              const price = calcCourtBookingPrice(zones, date, startTime, endTime, cfg)!;
              const isFree = price.total === 0;
              return (
                <div
                  className={`rounded-lg p-2.5 border ${isFree ? 'bg-bg-soft/40 border-line' : 'bg-emerald-50 border-emerald-200'}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span
                      className={`text-[10px] font-mono uppercase tracking-wider ${isFree ? 'text-ink-3' : 'text-emerald-800'}`}
                    >
                      {t('booking.edit.price')}
                    </span>
                    <span
                      className={`font-display text-lg font-bold ${isFree ? 'text-ink-2' : 'text-emerald-700'}`}
                    >
                      {isFree
                        ? t('booking.edit.free')
                        : t('booking.edit.priceKr', { n: String(price.total) })}
                    </span>
                  </div>
                  {price.breakdown.length > 1 && (
                    <div className="mt-1 text-[10px] text-emerald-800 space-y-0.5">
                      {price.breakdown.map((seg, i) => (
                        <div key={i}>
                          {t('booking.edit.priceRow', {
                            from: fromMinHHMM(seg.fromMin),
                            to: fromMinHHMM(seg.toMin),
                            zone: seg.zoneName,
                            kr: String(seg.kr),
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Booker fields — shown only in admin mode or edit. In the normal
              customer flow the booker is the logged-in user (login isn't
              implemented yet, but the fields should NOT be exposed to the end user
              so we don't build a flow we'll have to tear down later). */}
          {adminMode || mode === 'edit' ? (
            <>
              {adminMode && (
                <div className="pt-2 border-t border-line">
                  <label
                    htmlFor={ids.bookingTitle}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('booking.edit.titleAdmin')}
                  </label>
                  <input
                    id={ids.bookingTitle}
                    type="text"
                    className="input w-full"
                    value={bookingTitle}
                    onChange={(e) => setBookingTitle(e.target.value)}
                    placeholder={t('booking.edit.titlePlaceholder')}
                    // «Fyll inn navn eller tittel» — either field answers it, so both say so.
                    aria-invalid={invalidReason === 'identity' || undefined}
                    aria-describedby={
                      invalidReason === 'identity'
                        ? `${ids.bookingTitleHelp} ${ids.invalid}`
                        : ids.bookingTitleHelp
                    }
                  />
                  <p id={ids.bookingTitleHelp} className="text-[10px] text-ink-3 mt-1">
                    {t('booking.edit.titleHelp')}
                  </p>
                </div>
              )}
              <div className={adminMode ? '' : 'pt-2 border-t border-line'}>
                <label
                  htmlFor={ids.bookedBy}
                  className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                >
                  {t('booking.edit.bookedBy')}
                </label>
                <input
                  id={ids.bookedBy}
                  type="text"
                  className="input w-full"
                  aria-invalid={invalidReason === 'identity' || undefined}
                  aria-describedby={invalidReason === 'identity' ? ids.invalid : undefined}
                  value={bookerName}
                  onChange={(e) => setBookerName(e.target.value)}
                  placeholder={t('booking.edit.bookedByPlaceholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor={ids.phone}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('booking.edit.phone')}
                  </label>
                  <input
                    id={ids.phone}
                    type="tel"
                    className="input w-full"
                    value={bookerPhone}
                    onChange={(e) => setBookerPhone(e.target.value)}
                    placeholder={t('booking.edit.optional')}
                  />
                </div>
                <div>
                  <label
                    htmlFor={ids.email}
                    className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                  >
                    {t('booking.edit.email')}
                  </label>
                  <input
                    id={ids.email}
                    type="email"
                    className="input w-full"
                    value={bookerEmail}
                    onChange={(e) => setBookerEmail(e.target.value)}
                    placeholder={t('booking.edit.optional')}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="pt-2 border-t border-line text-[11px] text-ink-3 italic">
              {t('booking.edit.loggedInBooker')}
            </div>
          )}

          {/* EQUIPMENT WITH THE BOOKING (docs/domains/ASSET_BOOKING.md). Offered
              only when the venue has some. A machine is booked right after the
              court, attached to it, so a tick here is a choice and not a write;
              what is already attached is listed with a way to let it go. */}
          {assets.length > 0 && (
            <div className="rounded-xl border border-line p-3 flex flex-col gap-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                {t('booking.edit.assets')}
              </div>
              {attachedAssetBookings.length > 0 && (
                <ul className="flex flex-col gap-1">
                  <li className="text-[10px] text-ink-3">{t('booking.edit.assetsAttached')}</li>
                  {attachedAssetBookings.map((ab) => {
                    const a = assets.find((x) => x.id === ab.assetId);
                    return (
                      <li
                        key={ab.id}
                        className="flex items-center justify-between gap-2 text-sm text-ink-1"
                      >
                        <span className="truncate">
                          {a?.name ?? ab.assetId} · {ab.startTime}–{ab.endTime}
                          {ab.priceNok > 0
                            ? ` · ${t('booking.edit.assetPrice', { n: String(ab.priceNok) })}`
                            : ''}
                        </span>
                        {onRemoveAssetBooking && (
                          <button
                            type="button"
                            onClick={() => onRemoveAssetBooking(ab.id)}
                            className="text-xs rounded px-2 py-0.5 border border-line text-ink-3 hover:bg-bg-soft"
                          >
                            {t('booking.edit.assetRemove')}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="text-[10px] text-ink-3">{t('booking.edit.assetsHint')}</div>
              <div className="flex flex-col gap-1">
                {assets.map((a) => {
                  const free =
                    availableAssetsFor([a], assetBookings, { date, startTime, endTime }).length > 0;
                  const already = attachedAssetBookings.some((ab) => ab.assetId === a.id);
                  const offered = free && !already;
                  const checked = assetIds.includes(a.id);
                  const assetPrice = calcAssetBookingPrice(a, date, startTime, endTime, cfg).total;
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2 text-sm select-none ${offered ? 'cursor-pointer text-ink-1' : 'text-ink-3'}`}
                    >
                      <input
                        type="checkbox"
                        disabled={!offered}
                        checked={checked && offered}
                        onChange={(e) =>
                          setAssetIds((s) =>
                            e.target.checked ? [...s, a.id] : s.filter((x) => x !== a.id),
                          )
                        }
                      />
                      <span className="flex-1 truncate">{a.name}</span>
                      <span className="text-xs">
                        {!offered
                          ? t('booking.edit.assetTaken')
                          : assetPrice > 0
                            ? t('booking.edit.assetPrice', { n: String(assetPrice) })
                            : t('booking.edit.assetFree')}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label
              htmlFor={ids.notes}
              className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
            >
              {t('booking.edit.notes')}
            </label>
            <textarea
              id={ids.notes}
              className="input w-full"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('booking.edit.optional')}
              aria-describedby={ids.notesHelp}
            />
            {/* The courtside tablet draws a booking's notes under the booker's
                name (/pad/state keeps `notes`, #1263 decision 2026-11-02), so
                the person typing is told the field is not private. */}
            <p id={ids.notesHelp} className="text-[10px] text-ink-3 mt-1">
              {t('booking.edit.notesCourtHint')}
            </p>
          </div>

          {/* Optional: register a single match on this booking (customer create
              flow only). The booker picks 4 players + an ELO weight now; the
              result is entered later under Enkeltkamper. */}
          {showMatchStep && (
            <div className="pt-2 border-t border-line">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={registerMatch}
                  onChange={(e) => setRegisterMatch(e.target.checked)}
                  className="w-4 h-4 accent-lime-deep"
                />
                <span className="text-sm font-semibold text-ink-1">
                  {t('booking.edit.match.register')}
                </span>
              </label>
              <p className="text-[11px] text-ink-3 mt-1">{t('booking.edit.match.registerHelp')}</p>

              {registerMatch && (
                <div className="mt-3 space-y-3">
                  {/* Mode choice: local (pick now) vs activity (open signup) */}
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setMatchMode('local')}
                      aria-pressed={matchMode === 'local'}
                      className={`text-left p-2.5 rounded-lg border-2 transition-colors ${matchMode === 'local' ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                    >
                      <div className="text-xs font-semibold text-ink-1">
                        {t('booking.edit.match.modeLocal')}
                      </div>
                      <div className="text-[10px] text-ink-3">
                        {t('booking.edit.match.modeLocalSub')}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setMatchMode('activity')}
                      aria-pressed={matchMode === 'activity'}
                      className={`text-left p-2.5 rounded-lg border-2 transition-colors ${matchMode === 'activity' ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                    >
                      <div className="text-xs font-semibold text-ink-1">
                        {t('booking.edit.match.modeActivity')}
                      </div>
                      <div className="text-[10px] text-ink-3">
                        {t('booking.edit.match.modeActivitySub')}
                      </div>
                    </button>
                  </div>

                  <EloWeightSlider
                    value={matchEloWeightPct / 100}
                    onChange={(v) => setMatchEloWeightPct(Math.round(v * 100))}
                    recommended={200}
                    alwaysUnlocked
                  />

                  {/* Match rules — the DEFAULT for live scoring on Banepad. */}
                  <div className="rounded-lg border border-line p-3 space-y-3">
                    <div className="text-[11px] text-ink-3">{t('booking.edit.match.rules')}</div>
                    <div>
                      <div
                        id={ids.matchFormat}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
                      >
                        {t('booking.edit.match.format')}
                      </div>
                      <div
                        className="grid grid-cols-2 gap-1.5"
                        role="group"
                        aria-labelledby={ids.matchFormat}
                      >
                        {MATCH_FORMAT_OPTIONS.map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => setMatchFormat(f.id as 'bo3_super' | 'bo3_full')}
                            aria-pressed={matchFormat === f.id}
                            className={`text-left p-2 rounded-lg border-2 transition-colors ${matchFormat === f.id ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                          >
                            <div className="text-xs font-semibold text-ink-1">{f.label}</div>
                            <div className="text-[10px] text-ink-3">{f.sub}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div
                        id={ids.deuce}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
                      >
                        {t('booking.edit.match.deuce')}
                      </div>
                      <div
                        className="grid grid-cols-3 gap-1.5"
                        role="group"
                        aria-labelledby={ids.deuce}
                      >
                        {SERIE_DEUCE_ORDER.map((key: string) => (
                          <button
                            key={key}
                            type="button"
                            onClick={() =>
                              setDeuceMode(key as 'advantage' | 'golden' | 'starpoint')
                            }
                            aria-pressed={deuceMode === key}
                            className={`text-center p-2 rounded-lg border-2 transition-colors ${deuceMode === key ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                            title={SERIE_DEUCE[key]?.desc || ''}
                          >
                            <div className="text-xs font-semibold text-ink-1">
                              {SERIE_DEUCE[key]?.label}
                            </div>
                            {SERIE_DEUCE[key]?.sub && (
                              <div className="text-[10px] text-ink-3">{SERIE_DEUCE[key].sub}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* LOCAL: pick the four players now */}
                  {matchMode === 'local' && (
                    <>
                      <PlayerPicker
                        allPlayers={players}
                        team1={matchTeam1}
                        team2={matchTeam2}
                        onChangeTeam1={setMatchTeam1}
                        onChangeTeam2={setMatchTeam2}
                        onPersistNewPlayer={onPersistNewPlayer}
                      />
                      {!matchAllSelected && (
                        <div className="text-[11px] text-amber-700">
                          {t('booking.edit.match.pickFour')}
                        </div>
                      )}
                    </>
                  )}

                  {/* VIA AKTIVITETER: open enkeltmatch-activity (4 players, signup) */}
                  {matchMode === 'activity' && (
                    <div className="space-y-3">
                      <div className="text-[11px] text-ink-2 rounded-lg bg-bg-soft/50 border border-line px-3 py-2">
                        {t('booking.edit.match.pricePerPlayer')}{' '}
                        <span className="font-semibold">
                          {t('booking.edit.priceKr', { n: String(matchPerPlayerPrice) })}
                        </span>{' '}
                        <span className="text-ink-3">{t('booking.edit.match.priceNote')}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label
                            htmlFor={ids.deregister}
                            className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                          >
                            {t('booking.edit.match.deregisterHours')}
                          </label>
                          <input
                            id={ids.deregister}
                            type="number"
                            min={0}
                            className="input w-full text-sm"
                            value={matchDeregisterHours}
                            onChange={(e) => setMatchDeregisterHours(e.target.value)}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor={ids.signupClose}
                            className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                          >
                            {t('booking.edit.match.signupCloseHours')}
                          </label>
                          <input
                            id={ids.signupClose}
                            type="number"
                            min={0}
                            className="input w-full text-sm"
                            aria-invalid={matchActivityTooLate || undefined}
                            aria-describedby={matchActivityTooLate ? ids.tooLate : undefined}
                            value={matchSignupCloseHours}
                            onChange={(e) => setMatchSignupCloseHours(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <div
                          id={ids.levelReq}
                          className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
                        >
                          {t('booking.edit.match.levelReq')}
                        </div>
                        <div
                          className="grid grid-cols-2 gap-2"
                          role="group"
                          aria-labelledby={ids.levelReq}
                        >
                          <div>
                            <label
                              htmlFor={ids.levelMin}
                              className="block text-[10px] text-ink-3 mb-1"
                            >
                              {t('booking.edit.match.levelFrom')}
                            </label>
                            <select
                              id={ids.levelMin}
                              className="input w-full text-sm"
                              value={matchLevelMin}
                              onChange={(e) => setMatchLevelMin(e.target.value)}
                            >
                              <option value="">{t('booking.edit.match.levelAll')}</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                                <option key={n} value={n}>
                                  {t('booking.edit.match.level', { n: String(n) })}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label
                              htmlFor={ids.levelMax}
                              className="block text-[10px] text-ink-3 mb-1"
                            >
                              {t('booking.edit.match.levelTo')}
                            </label>
                            <select
                              id={ids.levelMax}
                              className="input w-full text-sm"
                              value={matchLevelMax}
                              onChange={(e) => setMatchLevelMax(e.target.value)}
                            >
                              <option value="">{t('booking.edit.match.levelAll')}</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                                <option key={n} value={n}>
                                  {t('booking.edit.match.level', { n: String(n) })}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <p className="text-[10px] text-ink-3 mt-1">
                          {t('booking.edit.match.levelHelp')}
                        </p>
                      </div>
                      <div>
                        <div
                          id={ids.matchPayment}
                          className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
                        >
                          {t('booking.edit.match.payment')}
                        </div>
                        <div
                          className="grid grid-cols-1 gap-1.5"
                          role="group"
                          aria-labelledby={ids.matchPayment}
                        >
                          <button
                            type="button"
                            onClick={() => setMatchPaymentMode('split')}
                            aria-pressed={matchPaymentMode === 'split'}
                            className={`text-left p-2.5 rounded-lg border-2 transition-colors ${matchPaymentMode === 'split' ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                          >
                            <div className="text-xs font-semibold text-ink-1">
                              {t('booking.edit.match.paySplit')}
                            </div>
                            <div className="text-[10px] text-ink-3">
                              {t('booking.edit.match.paySplitSub', {
                                n: String(matchPerPlayerPrice),
                              })}
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setMatchPaymentMode('booker_all')}
                            aria-pressed={matchPaymentMode === 'booker_all'}
                            className={`text-left p-2.5 rounded-lg border-2 transition-colors ${matchPaymentMode === 'booker_all' ? 'border-lime-deep bg-lime-bg/40' : 'border-line hover:border-line-soft'}`}
                          >
                            <div className="text-xs font-semibold text-ink-1">
                              {t('booking.edit.match.payAll')}
                            </div>
                            <div className="text-[10px] text-ink-3">
                              {t('booking.edit.match.payAllSub', {
                                n: String(matchPerPlayerPrice),
                              })}
                            </div>
                          </button>
                        </div>
                      </div>
                      <div className="text-[11px] text-ink-3">
                        {t('booking.edit.match.autoCancel')}
                      </div>
                      {matchActivityTooLate && (
                        <div
                          id={ids.tooLate}
                          className="text-[11px] text-rose rounded-lg bg-rose-soft/30 border border-rose/30 px-3 py-2"
                        >
                          {t('booking.edit.match.tooLate', { hours: matchSignupCloseHours })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Status selector — shown only in admin mode or when the user is editing
              an existing booking. In the normal create flow the payment
              modal sets the status automatically based on completed payment. */}
          {(adminMode || mode === 'edit') && (
            <div className="pt-2 border-t border-line">
              <div
                id={ids.paymentStatus}
                className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1.5"
              >
                {t('booking.edit.paymentStatus')}
              </div>
              <div
                className="grid grid-cols-3 gap-1.5"
                role="group"
                aria-labelledby={ids.paymentStatus}
              >
                {[
                  { id: 'free', label: t('booking.edit.status.free'), color: '#3b82f6' },
                  { id: 'pending', label: t('booking.edit.status.pending'), color: '#f59e0b' },
                  { id: 'paid', label: t('booking.edit.status.paid'), color: '#059669' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setPaymentStatus(opt.id)}
                    aria-pressed={paymentStatus === opt.id}
                    className={`text-xs font-semibold px-2 py-1.5 rounded border-2 transition-colors ${
                      paymentStatus === opt.id
                        ? 'text-white'
                        : 'bg-paper text-ink-2 border-line hover:border-ink-3'
                    }`}
                    style={
                      paymentStatus === opt.id
                        ? { background: opt.color, borderColor: opt.color }
                        : {}
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {paymentStatus === 'pending' && initial?.priceNok > 0 && (
                <button
                  onClick={() => setPaymentStatus('paid')}
                  className="mt-2 w-full text-xs font-semibold px-3 py-1.5 rounded border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors"
                >
                  {t('booking.edit.markPaid', { n: String(initial.priceNok) })}
                </button>
              )}
              {/* A disputed payment (#1051), beside the status it does not change. */}
              {paymentDisputesOf(initial).length > 0 && (
                <div className="mt-2">
                  <PaymentDisputeNotice disputes={paymentDisputesOf(initial)} />
                </div>
              )}
              {/* THE WAY A STAFF BOOKING GETS PAID (#1137): the member it is for
                  is sent a link and pays in the app. Only for a booking that
                  exists and is STORED as owed — the server decides the rest. */}
              {mode === 'edit' &&
                initial?.paymentStatus === 'pending' &&
                typeof initial?.id === 'string' && <SendPaymentLink bookingId={initial.id} />}
            </div>
          )}

          {/* Recurring — admin only, create mode. Repeat the booking on the same
              weekday/time for N weeks ahead (e.g. fixed training every Tuesday). */}
          {adminMode && (mode === 'create' || editScope === 'series') && (
            <div className="pt-2 border-t border-line">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recurring}
                  onChange={(e) => setRecurring(e.target.checked)}
                  className="w-4 h-4 accent-emerald-600"
                />
                <span className="text-sm font-semibold text-ink-1">
                  {t(
                    editScope === 'series'
                      ? 'booking.edit.recur.toggleSeries'
                      : 'booking.edit.recur.toggle',
                  )}
                </span>
              </label>
              {editScope === 'series' && recurring && (
                <p className="text-[10px] text-amber-700 mt-1 pl-6">
                  {t('booking.edit.recur.rebuildWarning')}
                </p>
              )}
              {recurring && (
                <div className="mt-2.5 pl-1 space-y-2.5">
                  {/* Frequency */}
                  <div>
                    <div
                      id={ids.recurFreq}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                    >
                      {t('booking.edit.recur.repeats')}
                    </div>
                    <div
                      className="flex gap-1.5 flex-wrap"
                      role="group"
                      aria-labelledby={ids.recurFreq}
                    >
                      {[
                        ['daily', t('booking.edit.recur.daily')],
                        ['weekly', t('booking.edit.recur.weekly')],
                        ['monthly', t('booking.edit.recur.monthly')],
                        ['yearly', t('booking.edit.recur.yearly')],
                      ].map(([v, lbl]) => (
                        <button
                          key={v}
                          onClick={() => setRecurFreq(v)}
                          aria-pressed={recurFreq === v}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors ${recurFreq === v ? 'bg-emerald-700 text-white' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line'}`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Interval */}
                  <div className="flex items-center gap-2">
                    <label htmlFor={ids.recurInterval} className="text-[11px] text-ink-2">
                      {t('booking.edit.recur.every')}
                    </label>
                    <input
                      id={ids.recurInterval}
                      // «Hver 2 uke(r)»: the label before, the unit after.
                      aria-describedby={ids.recurIntervalUnit}
                      type="number"
                      min={1}
                      max={52}
                      value={recurInterval}
                      onChange={(e) => setRecurInterval(Math.max(1, Number(e.target.value) || 1))}
                      className="input w-16 text-sm"
                    />
                    <span id={ids.recurIntervalUnit} className="text-[11px] text-ink-2">
                      {t(
                        recurFreq === 'daily'
                          ? 'booking.edit.recur.unitDay'
                          : recurFreq === 'weekly'
                            ? 'booking.edit.recur.unitWeek'
                            : recurFreq === 'monthly'
                              ? 'booking.edit.recur.unitMonth'
                              : 'booking.edit.recur.unitYear',
                      )}
                    </span>
                  </div>
                  {/* Weekdays (weekly only) */}
                  {recurFreq === 'weekly' && (
                    <div>
                      <div
                        id={ids.recurDays}
                        className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                      >
                        {t('booking.edit.recur.onDays')}
                      </div>
                      <div className="flex gap-1" role="group" aria-labelledby={ids.recurDays}>
                        {[
                          t('booking.edit.recur.day.mon'),
                          t('booking.edit.recur.day.tue'),
                          t('booking.edit.recur.day.wed'),
                          t('booking.edit.recur.day.thu'),
                          t('booking.edit.recur.day.fri'),
                          t('booking.edit.recur.day.sat'),
                          t('booking.edit.recur.day.sun'),
                        ].map((lbl, i) => {
                          const on = recurWeekdays.includes(i);
                          return (
                            <button
                              key={i}
                              onClick={() =>
                                setRecurWeekdays((prev) =>
                                  prev.includes(i)
                                    ? prev.filter((x) => x !== i)
                                    : [...prev, i].sort((a, b) => a - b),
                                )
                              }
                              aria-pressed={on}
                              className={`text-[11px] font-semibold w-9 h-8 rounded-lg transition-colors ${on ? 'bg-emerald-700 text-white' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line'}`}
                            >
                              {lbl}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-ink-3 mt-1">
                        {t('booking.edit.recur.daysHelp')}
                      </p>
                    </div>
                  )}
                  {/* End */}
                  <div>
                    <div
                      id={ids.recurEnds}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                    >
                      {t('booking.edit.recur.ends')}
                    </div>
                    <div
                      className="flex gap-1.5 mb-1.5"
                      role="group"
                      aria-labelledby={ids.recurEnds}
                    >
                      {[
                        ['count', t('booking.edit.recur.endCount')],
                        ['until', t('booking.edit.recur.endDate')],
                        ['never', t('booking.edit.recur.endNever')],
                      ].map(([v, lbl]) => (
                        <button
                          key={v}
                          onClick={() => setRecurEndType(v)}
                          aria-pressed={recurEndType === v}
                          className={`text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors ${recurEndType === v ? 'bg-ink text-white' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line'}`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    {recurEndType === 'count' && (
                      <div className="flex items-center gap-2">
                        <input
                          id={ids.recurCount}
                          aria-labelledby={`${ids.recurEnds} ${ids.recurCount} ${ids.recurCountUnit}`}
                          type="number"
                          min={1}
                          max={365}
                          value={recurCount}
                          onChange={(e) => setRecurCount(Math.max(1, Number(e.target.value) || 1))}
                          className="input w-20 text-sm"
                        />
                        <span id={ids.recurCountUnit} className="text-[11px] text-ink-2">
                          {t('booking.edit.recur.occurrences')}
                        </span>
                      </div>
                    )}
                    {recurEndType === 'until' && (
                      <input
                        aria-labelledby={ids.recurEnds}
                        type="date"
                        value={recurUntil}
                        min={date}
                        onChange={(e) => setRecurUntil(e.target.value)}
                        className="input text-sm"
                      />
                    )}
                    {recurEndType === 'never' && (
                      <p className="text-[10px] text-amber-700">
                        {t('booking.edit.recur.neverCap')}
                      </p>
                    )}
                  </div>
                  <div>
                    <div
                      id={ids.recurHoliday}
                      className="block text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1"
                    >
                      {t('booking.edit.recur.holidayWhen')}
                    </div>
                    <div className="flex gap-1.5" role="group" aria-labelledby={ids.recurHoliday}>
                      <button
                        onClick={() => setRecurAvoidHolidays(false)}
                        aria-pressed={!recurAvoidHolidays}
                        className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${!recurAvoidHolidays ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line'}`}
                      >
                        {t('booking.edit.recur.holidayInclude')}
                      </button>
                      <button
                        onClick={() => setRecurAvoidHolidays(true)}
                        aria-pressed={recurAvoidHolidays}
                        className={`flex-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${recurAvoidHolidays ? 'bg-emerald-700 text-white shadow' : 'bg-bg-soft/50 text-ink-2 hover:bg-bg-soft border border-line'}`}
                      >
                        {t('booking.edit.recur.holidaySkip')}
                      </button>
                    </div>
                    <p className="text-[10px] text-ink-3 mt-1">
                      {t('booking.edit.recur.holidaySet', {
                        country: (cfg.holidayCountry || 'no').toUpperCase(),
                      })}
                    </p>
                  </div>
                  <p className="text-[10px] text-ink-3 italic">
                    {t('booking.edit.recur.collisions')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e7eae3',
            background: '#fafbf7',
            display: 'flex',
            gap: 8,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            {mode === 'edit' && onRefund ? (
              <button
                type="button"
                onClick={() => setConfirmRefund(true)}
                data-testid="booking-refund-open"
                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ border: '1px solid #dc2626', color: '#dc2626', background: 'transparent' }}
              >
                {paidAmount
                  ? t('booking.edit.refund.openAmount', {
                      amount: formatNumber(paidAmount.amountMinor / 100, {
                        style: 'currency',
                        currency: paidAmount.currency,
                        minimumFractionDigits: 0,
                      }),
                    })
                  : t('booking.edit.refund.open')}
              </button>
            ) : (
              mode === 'edit' &&
              onDelete && (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                  style={{
                    border: '1px solid #dc2626',
                    color: '#dc2626',
                    background: 'transparent',
                  }}
                >
                  {t('booking.edit.cancelBooking')}
                </button>
              )
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <button onClick={onClose} className="btn-outline text-sm">
                {t('booking.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!isValid}
                aria-describedby={invalidReason ? ids.invalid : undefined}
                className="btn-lime text-sm disabled:opacity-40"
              >
                {(() => {
                  if (mode === 'edit') return t('booking.edit.save');
                  if (adminMode)
                    return t(recurring ? 'booking.edit.checkAndCreate' : 'booking.edit.create');
                  // Via-aktiviteter + split: the booker is NOT charged the full
                  // price now (the four players each pay their share later), so
                  // this is a plain "create", not a payment.
                  const splitActivity =
                    showMatchStep &&
                    registerMatch &&
                    matchMode === 'activity' &&
                    matchPaymentMode === 'split';
                  const price = calcCourtBookingPrice(zones, date, startTime, endTime, cfg);
                  if (splitActivity) return t('booking.edit.createActivity');
                  return price && price.total > 0
                    ? t('booking.edit.toPayment', { n: String(price.total) })
                    : t('booking.edit.create');
                })()}
              </button>
            </div>
            {invalidReason && (
              <span id={ids.invalid} className="text-[10px] text-rose text-right">
                {t(INVALID_MESSAGE[invalidReason])}
              </span>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
